import MainFrame from "@/Layout/Layout";
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";
import {
  faChevronDown,
  faChevronUp,
  faEnvelope,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import sendEmail from "@/services/EmailingServices";

const GlobalHQInfo: React.FC = () => {
  return (
    <div className="h-full space-y-8 rounded-lg bg-white p-8 shadow-lg">
      <h2 className="text-3xl font-extrabold text-gray-900">
        Nossas Informações
      </h2>

      <div className="space-y-2">
        <p className="text-xl font-semibold text-gray-800">
          Restart Design & Consulting Ltda.
        </p>
        <p className="w-2/3 leading-relaxed text-gray-700">
          Avenida Estrela do Mar, 59, Praia de Guanabara, Anchieta - ES, Brasil
        </p>
      </div>

      {/*<div className="space-y-2">*/}
      {/*  <p className="text-xl font-bold text-gray-800">Phone</p>*/}
      {/*  <div className="ml-4 flex items-center space-x-4 text-lg text-gray-700">*/}
      {/*    <FontAwesomeIcon icon={faPhone} />*/}
      {/*    <a*/}
      {/*      href="tel:+5528992482421"*/}
      {/*      className="text-blue-600 hover:underline"*/}
      {/*    >*/}
      {/*      +55 28 99248-2421*/}
      {/*    </a>*/}
      {/*  </div>*/}
      {/*</div>*/}

      <div className="space-y-2">
        <p className="text-xl font-bold text-gray-800"> E-mail</p>
        <div className="ml-4 flex items-center space-x-4 text-lg text-gray-700">
          <FontAwesomeIcon icon={faEnvelope} />
          <a
            href="mailto:contact@rdec.com.br"
            className="text-blue-600 hover:underline"
          >
            contact@rdec.com.br
          </a>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xl font-bold text-gray-800">Mídias Sociais</p>
        <div className="ml-4 flex items-center space-x-4 text-lg text-gray-700">
          <FontAwesomeIcon icon={faLinkedin} className="text-blue-700" />
          <a
            href="https://linkedin.com"
            target="_blank"
            rel="noreferrer"
            className="hover:text-blue-500 hover:underline"
          >
            LinkedIn
          </a>
        </div>
      </div>
    </div>
  );
};

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    jobTitle: "",
    company: "",
    agree: false,
    selectedSystems: {
      valves: false,
      automations: false,
      sensors_gateways: false,
      pneumatics: false,
      clamps: false,
      cleaning: false,
    },
  });

  const [isLiftSectionOpen, setIsLiftSectionOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    if (name in formData.selectedSystems) {
      setFormData({
        ...formData,
        selectedSystems: {
          ...formData.selectedSystems,
          [name]: checked,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : value,
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.agree) {
      alert("You must agree to the terms to submit the form.");
      return;
    }

    setLoading(true);

    const selectedSystems =
      Object.entries(formData.selectedSystems)
        .filter(([_, value]) => value)
        .map(([key]) => key.charAt(0).toUpperCase() + key.slice(1))
        .join("; ") || "Not specified";

    // Enviar cada informação separadamente no templateParams
    const templateParams = {
      from_name: formData.fullName || "Anonymous",
      to_name: "Alexandre Lorega",
      full_name: formData.fullName || "Not provided",
      email: formData.email || "Not provided",
      phone_number: formData.phoneNumber || "Not provided",
      job_title: formData.jobTitle || "Not provided",
      company: formData.company || "Not provided",
      interested_in: selectedSystems,
    };

    sendEmail(templateParams)
      .then(() => {
        alert("Formulário enviado com sucesso!");
        setFormData({
          fullName: "",
          email: "",
          phoneNumber: "",
          jobTitle: "",
          company: "",
          agree: false,
          selectedSystems: {
            valves: false,
            automations: false,
            sensors_gateways: false,
            pneumatics: false,
            clamps: false,
            cleaning: false,
          },
        });
        setLoading(false);
      })
      .catch((error) => {
        console.error("Email sending failed:", error);
        alert("Falha ao enviar o formulário. Tente novamente.");
        setLoading(false);
      });
  };

  const toggleLiftSection = () => {
    setIsLiftSectionOpen(!isLiftSectionOpen);
  };

  return (
    <form
      className="h-full w-full max-w-4xl rounded-lg bg-offwhite p-8 text-black shadow-lg"
      onSubmit={handleSubmit}
    >
      <div className={`mb-6 flex w-full flex-col gap-3`}>
        <span className="text-center text-2xl font-bold">
          Solicite sua Demonstração
        </span>
        <span className="text-center text-sm">
          Preencha o formulário abaixo para solicitar uma demonstração de nossos
          produtos e sistemas.
        </span>
      </div>
      <label htmlFor="fullName" className="mb-2 block text-sm font-semibold">
        Nome Completo <span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        id="fullName"
        name="fullName"
        value={formData.fullName}
        placeholder={`Meu Nome Completo`}
        onChange={handleChange}
        className="mb-4 w-full rounded-md p-2 text-black focus:outline-none focus:ring-2 focus:ring-primary-700"
        required
      />
      <div className={`flex w-full flex-col gap-3 lg:flex-row`}>
        <div className={`flex w-full flex-col`}>
          <label
            htmlFor="phoneNumber"
            className="mb-2 block text-sm font-semibold"
          >
            Telefone <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            placeholder={`+55 (99) 99999-9999`}
            className="mb-4 w-full rounded-md p-2 text-black focus:outline-none focus:ring-2 focus:ring-primary-700"
            required
          />
        </div>

        <div className={`flex w-full flex-col`}>
          <label htmlFor="email" className="mb-2 block text-sm font-semibold">
            E-mail Profissional<span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={`exemplo@email.com`}
            className="mb-4 w-full rounded-md p-2 text-black focus:outline-none focus:ring-2 focus:ring-primary-700"
            required
          />
        </div>
      </div>
      <div className={`flex w-full flex-col gap-3 lg:flex-row`}>
        <div className={`flex w-full flex-col`}>
          <label
            htmlFor="jobTitle"
            className="mb-2 block text-sm font-semibold"
          >
            Cargo
          </label>
          <input
            type="text"
            id="jobTitle"
            name="jobTitle"
            placeholder={`Meu Cargo`}
            value={formData.jobTitle}
            onChange={handleChange}
            className="mb-4 w-full rounded-md p-2 text-black focus:outline-none focus:ring-2 focus:ring-primary-700"
          />
        </div>
        <div className={`flex w-full flex-col`}>
          <label htmlFor="company" className="mb-2 block text-sm font-semibold">
            Setor da Indústria
          </label>
          <input
            type="text"
            id="company"
            name="company"
            placeholder={`Minha Empresa`}
            value={formData.company}
            onChange={handleChange}
            className="mb-4 w-full rounded-md p-2 text-black focus:outline-none focus:ring-2 focus:ring-primary-700"
          />
        </div>
      </div>

      <div className="mb-4 select-none">
        <button
          type="button"
          onClick={toggleLiftSection}
          className="flex w-full items-center gap-3 rounded-md bg-offwhite-300 px-4 py-2 text-left font-semibold focus:outline-none focus:ring-2 focus:ring-primary-700"
        >
          <FontAwesomeIcon
            icon={isLiftSectionOpen ? faChevronUp : faChevronDown}
          />
          Selecione a solução de maior interesse
        </button>
        {isLiftSectionOpen && (
          <motion.div
            className="flex w-full flex-wrap gap-4 text-nowrap p-4"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <label className="block">
              <input
                type="checkbox"
                name="valves"
                checked={formData.selectedSystems.valves}
                onChange={handleChange}
                className="mr-2 rounded"
              />
              Válvulas
            </label>
            <label className="block">
              <input
                type="checkbox"
                name="automations"
                checked={formData.selectedSystems.automations}
                onChange={handleChange}
                className="mr-2 rounded"
              />
              Automações HIMA
            </label>
            <label className="block">
              <input
                type="checkbox"
                name="sensors_gateways"
                checked={formData.selectedSystems.sensors_gateways}
                onChange={handleChange}
                className="mr-2 rounded"
              />
              Sensores/Gateways
            </label>
            <label className="block">
              <input
                type="checkbox"
                name="pneumatics"
                checked={formData.selectedSystems.pneumatics}
                onChange={handleChange}
                className="mr-2 rounded"
              />
              Atuadores Pneumáticos
            </label>
            <label className="block">
              <input
                type="checkbox"
                name="clamps"
                checked={formData.selectedSystems.clamps}
                onChange={handleChange}
                className="mr-2 rounded"
              />
              Clamp Hub Connector
            </label>
            <label className="block">
              <input
                type="checkbox"
                name="cleaning"
                checked={formData.selectedSystems.cleaning}
                onChange={handleChange}
                className="mr-2 rounded"
              />
              Limpeza de Painéis
            </label>
          </motion.div>
        )}
      </div>

      <div className="mb-6 flex items-center">
        <input
          type="checkbox"
          id="agree"
          name="agree"
          checked={formData.agree}
          onChange={handleChange}
          className="mr-2 rounded"
        />
        <label htmlFor="agree" className="text-sm">
          Eu concordo com a RD&C para entrar em contato comigo com informações
          sobre os serviços de consultoria e seus produtos.
        </label>
      </div>
      <button
        type="submit"
        className="w-full rounded-md bg-primary-700 px-4 py-2 font-semibold text-white hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-700"
        disabled={loading}
      >
        {loading ? "Enviando..." : "SOLICITAR DEMONSTRAÇÃO"}
      </button>
    </form>
  );
};

export const SimplifiedContactForm = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    jobTitle: null,
    company: null,
    selectedSystems: {
      valves: false,
      automations: false,
      sensors_gateways: false,
      pneumatics: false,
      clamps: false,
      cleaning: false,
    },
  });

  const [isLiftSectionOpen, setIsLiftSectionOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggleLiftSection = () => {
    setIsLiftSectionOpen(!isLiftSectionOpen);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (name in formData.selectedSystems) {
      setFormData({
        ...formData,
        selectedSystems: {
          ...formData.selectedSystems,
          [name]: checked,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : value,
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);

    const selectedSystems =
      Object.entries(formData.selectedSystems)
        .filter(([_, value]) => value)
        .map(([key]) => key.charAt(0).toUpperCase() + key.slice(1))
        .join("; ") || "Not specified";

    // Enviar cada informação separadamente no templateParams
    const templateParams = {
      from_name: formData.fullName || "Anonymous",
      to_name: "Alexandre Lorega",
      full_name: formData.fullName || "Not provided",
      email: formData.email || "Not provided",
      phone_number: formData.phoneNumber || "Not provided",
      job_title: formData.jobTitle || "Not provided",
      company: formData.company || "Not provided",
      interested_in: selectedSystems,
    };

    sendEmail(templateParams)
      .then(() => {
        alert("Formulário enviado com sucesso!");
        setFormData({
          fullName: "",
          email: "",
          phoneNumber: "",
          jobTitle: null,
          company: null,
          selectedSystems: {
            valves: false,
            automations: false,
            sensors_gateways: false,
            pneumatics: false,
            clamps: false,
            cleaning: false,
          },
        });
        setLoading(false);
      })
      .catch((error) => {
        console.error("Email sending failed:", error);
        alert("Falha ao enviar o formulário. Tente novamente.");
        setLoading(false);
      });
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-gradient-to-r from-primary-700 to-primary-900 p-8 lg:p-0">
      <form
        className="w-full rounded-lg bg-offwhite p-8 text-black shadow-lg lg:max-w-4xl"
        onSubmit={handleSubmit}
      >
        <h2 className="mb-4 text-center text-2xl font-bold">
          Solicite seu Contato
        </h2>
        <p className="mb-6 text-center">
          Preencha o formulário abaixo para solicitar uma demonstração de nossos
          produtos e sistemas.
        </p>

        <div className={`flex w-full flex-col gap-3 lg:flex-row`}>
          <div className={`flex w-full flex-col`}>
            <label
              htmlFor="fullName"
              className="mb-2 block text-sm font-semibold"
            >
              Nome Completo <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              placeholder={`Meu Nome Completo`}
              value={formData.fullName}
              onChange={handleChange}
              className="w-full rounded-md p-2 text-black focus:outline-none focus:ring-2 focus:ring-primary-700"
              required
            />
          </div>
          <div className={`flex w-full flex-col`}>
            <label htmlFor="email" className="mb-2 block text-sm font-semibold">
              E-mail Profissional <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={`exemplo@email.com`}
              className="mb-4 w-full rounded-md p-2 text-black focus:outline-none focus:ring-2 focus:ring-primary-700"
              required
            />
          </div>
        </div>

        <label
          htmlFor="contactType"
          className="mb-2 block text-sm font-semibold"
        >
          Telefone <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          id="phoneNumber"
          name="phoneNumber"
          placeholder={`+55 (99) 99999-9999`}
          value={formData.phoneNumber}
          onChange={handleChange}
          className="mb-4 w-full rounded-md p-2 text-black focus:outline-none focus:ring-2 focus:ring-primary-700"
          required
        />

        <div className="mb-4 select-none">
          <button
            type="button"
            onClick={toggleLiftSection}
            className="flex w-full items-center gap-3 rounded-md bg-offwhite-300 px-4 py-2 text-left font-semibold focus:outline-none focus:ring-2 focus:ring-primary-700"
          >
            <FontAwesomeIcon
              icon={isLiftSectionOpen ? faChevronUp : faChevronDown}
            />
            Selecione a solução de maior interesse
          </button>
          {isLiftSectionOpen && (
            <motion.div
              className="flex w-full flex-wrap gap-4 p-4"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <label className="block">
                <input
                  type="checkbox"
                  name="valves"
                  checked={formData.selectedSystems.valves}
                  onChange={handleChange}
                  className="mr-2 rounded"
                />
                Válvulas
              </label>
              <label className="block">
                <input
                  type="checkbox"
                  name="automations"
                  checked={formData.selectedSystems.automations}
                  onChange={handleChange}
                  className="mr-2 rounded"
                />
                Automações HIMA
              </label>
              <label className="block">
                <input
                  type="checkbox"
                  name="sensors_gateways"
                  checked={formData.selectedSystems.sensors_gateways}
                  onChange={handleChange}
                  className="mr-2 rounded"
                />
                Sensores/Gateways
              </label>
              <label className="block">
                <input
                  type="checkbox"
                  name="pneumatics"
                  checked={formData.selectedSystems.pneumatics}
                  onChange={handleChange}
                  className="mr-2 rounded"
                />
                Atuadores Pneumáticos
              </label>
              <label className="block">
                <input
                  type="checkbox"
                  name="clamps"
                  checked={formData.selectedSystems.clamps}
                  onChange={handleChange}
                  className="mr-2 rounded"
                />
                Clamp Hub Connector
              </label>
              <label className="block">
                <input
                  type="checkbox"
                  name="cleaning"
                  checked={formData.selectedSystems.cleaning}
                  onChange={handleChange}
                  className="mr-2 rounded"
                />
                Limpeza de Painéis
              </label>
            </motion.div>
          )}
        </div>

        <p className="mb-4 text-sm">
          Ao clicar em enviar, você concorda com os termos de serviço e a
          política de privacidade da RD&C.
        </p>

        <button
          type="submit"
          className="w-full rounded-md bg-primary-700 px-4 py-2 font-semibold text-white hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-700"
        >
          {loading ? "Enviando..." : "Enviar"}
        </button>
      </form>
    </div>
  );
};

const ContactUsView = () => {
  return (
    <MainFrame>
      <div className={`flex h-full w-full flex-col bg-offwhite`}>
        <div className="h-full w-full">
          <img
            src={`https://placehold.co/1080x200/006380/ffffff?text=Restart+Design+%26+Consulting`}
            className={`w-full`}
          />
        </div>
        <div
          className={`flex w-full flex-col items-center justify-center py-10`}
        >
          <span
            className={`items center flex w-1/2 flex-col justify-center gap-4 text-center`}
          >
            <p className={`text-center text-4xl font-bold text-black`}>
              Como podemos te ajudar?
            </p>
            <p className={`text-center text-black`}>
              Esse é um espaço direto com a RD&C. Preencha o formulário abaixo
              para solicitar seu contato, demonstrativo, e muito mais.
              Agradecemos sua mensagem!
            </p>
          </span>
        </div>
        <div
          className={`flex h-full flex-col-reverse justify-center gap-4 bg-offwhite-300 px-8 py-14 lg:flex-row`}
        >
          <GlobalHQInfo />
          <ContactForm />
        </div>
      </div>
    </MainFrame>
  );
};

export default ContactUsView;
